// AdjustmentsOfDataChunkedUploader.ts - Optimizirana klasa za chunked upload Adjustments podataka
import {
  getAuthToken,
  translateMessage,
  createSocketManager,
  validateCSVFormat,
  validateCSVSecurity,
  splitContentIntoChunks,
  encodeChunk,
  calculateBackoff,
  generateUploadId,
  CHUNK_SIZES,
  type SocketManager,
  type ProgressHandler
} from '@/core/services/upload/utils';
import type {
  AdjustmentsUploadProgress,
  AdjustmentsChunkMetadata,
  AdjustmentsProcessingParams,
  AdjustmentsServerResponse
} from '@/types/common';

// Row shapes streamed back to the caller, derived from the canonical response
// type so the streaming buffers stay assignable to AdjustmentsServerResponse.data.
type AdjustmentsResponseData = NonNullable<AdjustmentsServerResponse['data']>;
type AdjustmentsInfoRow = AdjustmentsResponseData['info_df'][number];
type AdjustmentsDataframeRow = AdjustmentsResponseData['dataframe'][number];

/** Shape of the Socket.IO event payloads emitted during streaming processing. */
interface AdjustmentsSocketData {
  uploadId: string;
  filename: string;
  // file_result
  info_record?: AdjustmentsInfoRow;
  chunked?: boolean;
  totalChunks?: number;
  dataframe_chunk?: AdjustmentsDataframeRow[];
  // dataframe_chunk
  chunkIndex?: number;
  chunk?: AdjustmentsDataframeRow[];
  // progress
  progress?: number;
  status?: string;
  phase?: AdjustmentsUploadProgress['phase'];
  currentFile?: number;
  totalFiles?: number;
  step?: string;
  eta?: number;
  etaFormatted?: string;
  message?: string;
  messageKey?: string;
  messageParams?: Record<string, unknown>;
  detail?: string;
  detailKey?: string;
  detailParams?: Record<string, unknown>;
}

/**
 * Optimizirana klasa za chunked upload Adjustments podataka
 * Kombinira najbolje funkcionalnosti iz postojećih uploadera
 */
export class AdjustmentsOfDataChunkedUploader {
  // Konstante
  // 5MB chunks, kao i ostali uploaderi. LARGE (8 MiB) je bio točno jednak
  // backendovom MAX_CHUNK_BYTES, pa je svaki bajt overheada značio 413
  // "chunk exceeds maximum size" — MEDIUM ostavlja 3 MiB rezerve.
  private static readonly CHUNK_SIZE = CHUNK_SIZES.MEDIUM; // 5MB chunks
  private static readonly RETRY_BASE_DELAY = 1000;

  private static get API_BASE_URL(): string {
    return import.meta.env.VITE_API_URL?.replace(/\/+$/, '') || '';
  }

  // Socket manager instance
  private static socketManager: SocketManager | null = null;
  // Progress tracking to prevent regression
  private static highestProgress: number = 0;

  /**
   * Get or create socket manager instance
   */
  private static getSocketManager(): SocketManager {
    if (!this.socketManager) {
      this.socketManager = createSocketManager({
        timeout: 300000, // 5 minutes
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 5
      });
    }
    return this.socketManager;
  }

  /**
   * Zatvaranje Socket.IO konekcije i cleanup room-a
   */
  public static disconnectSocket(uploadId?: string): void {
    if (this.socketManager) {
      this.socketManager.disconnect(uploadId);
      this.socketManager = null;
    }
  }

  /**
   * Upload jednog chunk-a s retry logikom
   */
  private static async uploadChunk(
    chunk: Uint8Array,
    metadata: AdjustmentsChunkMetadata,
    maxRetries: number = 3
  ): Promise<Response> {
    const formData = new FormData();

    // Dodaj chunk kao blob (type assertion to fix TypeScript compatibility)
    const chunkBlob = new Blob([chunk as BlobPart]);
    formData.append("files[]", chunkBlob);
    
    // Dodaj chunk metadata
    formData.append("uploadId", metadata.uploadId);
    formData.append("chunkIndex", metadata.chunkIndex.toString());
    formData.append("totalChunks", metadata.totalChunks.toString());
    formData.append("filename", metadata.filename);

    // Get authentication token
    const accessToken = await getAuthToken();

    let lastError: Error | undefined;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 300000); // 5 minute timeout

      try {
        // Upload attempt

        const response = await fetch(`${this.API_BASE_URL}/api/adjustmentsOfData/upload-chunk`, {
          method: "POST",
          body: formData,
          headers: {
            Accept: "application/json",
            'Authorization': `Bearer ${accessToken}`
          },
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        // Chunk uploaded
        
        if (!response.ok) {
          // Capture structured BE error info if present (W6/T3 contract: {error, error_code, suggestions}).
          let errorMsg = `Chunk upload failed: ${response.status} ${response.statusText}`;
          let errorCode: string | undefined;
          let suggestion: string | undefined;
          try {
            const body = await response.clone().json();
            if (typeof body?.error === 'string') errorMsg = body.error;
            if (typeof body?.error_code === 'string') errorCode = body.error_code;
            if (Array.isArray(body?.suggestions) && typeof body.suggestions[0] === 'string') {
              suggestion = body.suggestions[0];
            }
          } catch {
            /* response had no JSON body — fall back to status-line message */
          }
          const err = new Error(errorMsg) as Error & { code?: string; status?: number; suggestion?: string };
          err.status = response.status;
          if (errorCode) err.code = errorCode;
          if (suggestion) err.suggestion = suggestion;
          throw err;
        }

        return response;
      } catch (error: unknown) {
        clearTimeout(timeoutId);
        lastError = error instanceof Error ? error : new Error('Unknown error');
        
        if (error instanceof Error && error.name === 'AbortError') {
          lastError = new Error('Request timeout - server not responding');
        }
        // Non-abort errors are already captured in lastError above

        // Do NOT retry 4xx client errors (e.g. 413 payload too large,
        // 429 rate limited). The backend's answer won't change on
        // replay, so retrying only burns ~30s of backoff while hiding
        // the real error from the user.
        const status = (error as { status?: number } | undefined)?.status;
        if (status !== undefined && status >= 400 && status < 500) {
          throw lastError;
        }

        if (attempt === maxRetries) {
          throw lastError;
        }
        
        // Wait before retrying (exponential backoff)
        const delay = calculateBackoff(attempt, this.RETRY_BASE_DELAY);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    throw lastError || new Error('Max retries exceeded');
  }

  /**
   * Upload datoteke s chunked pristupom
   */
  public static async uploadFile(
    file: File,
    onProgress?: (progress: AdjustmentsUploadProgress) => void,
    sharedUploadId?: string
  ): Promise<AdjustmentsServerResponse> {
    // Use provided shared upload_id or generate new one
    const uploadId = sharedUploadId || generateUploadId('adj');

    // Inicijaliziraj Socket.IO konekciju
    const socketManager = this.getSocketManager();

    // FIX P1: Register Socket.IO listeners BEFORE any upload to prevent event loss
    socketManager.joinRoom(uploadId);

    // NOTE: During upload phase, we IGNORE backend Socket.IO progress events
    // Backend sends progress for file_combination and file_analysis during upload,
    // but this conflicts with frontend chunk upload progress (causes jumps like 50%→25%→28%→100%)
    // Upload progress is purely frontend-driven based on chunk upload completion
    // Backend progress is only used during processData() phase
    const progressHandler = (data: Record<string, unknown>) => {
      if (data.uploadId === uploadId) {
        // Log for debugging but DO NOT update progress during upload phase
      }
    };

    socketManager.onProgress(progressHandler);

    try {
      // Čitanje sadržaja datoteke
      const fileContent = await file.text();

      // 1. Sigurnosna validacija (CSV injection prevention)
      const securityValidation = validateCSVSecurity(fileContent);
      if (!securityValidation.isValid) {
        throw new Error(`Security validation failed: ${securityValidation.error}`);
      }

      // 2. Validacija CSV formata
      const formatValidation = validateCSVFormat(fileContent, { minColumns: 2, minLines: 2 });
      if (!formatValidation.isValid) {
        throw new Error(`CSV format validation failed: ${formatValidation.error}`);
      }

      // Podijeli sadržaj u chunkove
      const chunks = splitContentIntoChunks(fileContent, this.CHUNK_SIZE);
      const totalChunks = chunks.length;

      if (totalChunks === 0) {
        throw new Error('Empty file');
      }

      // Splitting file into chunks

      // Progress callback za početak

      onProgress?.({
        uploadProgress: 0,
        processingProgress: 0,
        status: 'uploading',
        phase: 'file_upload',
        message: 'Starting file upload...'
      });

      let finalResponse: Response | undefined;
      const totalSize = encodeChunk(fileContent).length;

      // Upload chunk-ova
      for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
        const chunkContent = chunks[chunkIndex];
        const chunkBytes = encodeChunk(chunkContent);

        const metadata: AdjustmentsChunkMetadata = {
          uploadId,
          chunkIndex,
          totalChunks,
          chunkSize: chunkBytes.length,
          totalSize,
          filename: file.name
        };

        // Uploading chunk

        try {
          const response = await this.uploadChunk(chunkBytes, metadata);
          // Chunk uploaded successfully
          finalResponse = response;

          // Ažuriranje progresa (0-100% za upload)
          const progress = ((chunkIndex + 1) / totalChunks) * 100;
          onProgress?.({
            uploadProgress: progress,
            processingProgress: 0,
            status: 'uploading',
            phase: 'file_upload',
            message: `Uploading chunk ${chunkIndex + 1}/${totalChunks} (${progress.toFixed(0)}%)`
          });

        } catch (chunkError) {

          onProgress?.({
            uploadProgress: ((chunkIndex + 1) / totalChunks) * 100,
            processingProgress: 0,
            status: 'error',
            phase: 'file_upload',
            error: `Chunk ${chunkIndex + 1}/${totalChunks} upload failed: ${chunkError instanceof Error ? chunkError.message : 'Unknown error'}`
          });
          throw chunkError;
        }
      }

      if (!finalResponse) {
        throw new Error('No response received from server');
      }

      // Procesiranje finalnog odgovora
      const responseData = await finalResponse.json();
      // Backend response received

      if (responseData.error) {
        onProgress?.({
          uploadProgress: 100,
          processingProgress: 0,
          status: 'error',
          phase: 'file_upload',
          error: responseData.error
        });
        return { error: responseData.error };
      }

      // Uspješan upload
      onProgress?.({
        uploadProgress: 100,
        processingProgress: 0,
        status: 'completed',
        phase: 'file_upload',
        message: 'File upload completed successfully'
      });

      // FIX P4: Cleanup Socket.IO listener with specific handler
      socketManager.offProgress(progressHandler);
      socketManager.leaveRoom(uploadId);

      // FIX P2: Always return uploadId in response to ensure consistency
      // Extract info_df from response based on backend structure
      let info_df: Array<{
        "Name der Datei": string;
        "Name der Messreihe": string;
        "Startzeit (UTC)": string;
        "Endzeit (UTC)": string;
        "Zeitschrittweite [min]": number;
        "Offset [min]": number;
        "Anzahl der Datenpunkte": number;
        "Anzahl der numerischen Datenpunkte": number;
        "Anteil an numerischen Datenpunkten": number;
      }> = [];

      if (responseData.data && responseData.data.info_df) {
        info_df = responseData.data.info_df;
      } else if (responseData.info_df) {
        info_df = responseData.info_df;
      }

      // Extracted info_df

      return {
        success: true,
        data: {
          info_df: info_df,
          dataframe: [], // Empty array for upload phase, will be populated during processing
          upload_id: uploadId  // FIX P2: Always include uploadId for consistent room management
        },
        message: 'File uploaded successfully'
      };

    } catch (error) {
      // FIX P4: Cleanup Socket.IO listener with specific handler in error case
      socketManager.offProgress(progressHandler);
      socketManager.leaveRoom(uploadId);

      let errorMessage = 'Connection error';

      if (!window.navigator.onLine) {
        errorMessage = 'No internet connection';
      } else if (error instanceof TypeError && error.message === "Failed to fetch") {
        errorMessage = 'Server not reachable';
      } else {
        errorMessage = `Unexpected error: ${error instanceof Error ? error.message : 'Unknown error'}`;
      }

      onProgress?.({
        uploadProgress: 0,
        processingProgress: 0,
        status: 'error',
        phase: 'file_upload',
        error: errorMessage
      });

      return { error: errorMessage };
    }
  }

  /**
   * Procesiranje podataka s chunked pristupom
   */
  public static async processData(
    params: AdjustmentsProcessingParams,
    uploadIds: { [filename: string]: string },
    fileNames: string[],
    onProgress?: (progress: AdjustmentsUploadProgress) => void
  ): Promise<AdjustmentsServerResponse> {
    // Reset progress tracking for new processing
    this.highestProgress = 0;

    // Get upload IDs for Socket.IO room subscription
    const uploadIdsList = Object.values(uploadIds);

    // Define handlers at top level for cleanup access
    let progressHandler: ProgressHandler | null = null;
    let fileResultHandler: ((data: AdjustmentsSocketData) => void) | null = null;
    let dataframeChunkHandler: ((data: AdjustmentsSocketData) => void) | null = null;
    let dataframeCompleteHandler: ((data: AdjustmentsSocketData) => void) | null = null;
    let fileErrorHandler: ((data: AdjustmentsSocketData) => void) | null = null;

    try {
      // Get authentication token
      const accessToken = await getAuthToken();

      const CHUNK_SIZE = 5000; // Process 5000 data points at a time
      const totalChunks = Math.ceil(fileNames.length / CHUNK_SIZE);

      // Initialize Socket.IO and join room for real-time progress
      const socketManager = this.getSocketManager();

      // FIX P3: Join rooms FIRST before any processing requests
      uploadIdsList.forEach(uploadId => {
        socketManager.joinRoom(uploadId);
      });

      // FIX P3: Storage for streaming results that will be accessed by waitForStreamingResults()
      // Initialize BEFORE registering listeners to prevent race conditions
      const streamingResults = {
        info_df: [] as AdjustmentsInfoRow[],
        dataframe: [] as AdjustmentsDataframeRow[],
      };
      let filesReceived = 0;
      const failedFiles: string[] = [];
      const fileChunks = new Map<string, { chunks: { index: number; data: AdjustmentsDataframeRow[] }[]; totalChunks: number }>();
      const totalFiles = fileNames.length;

      // Helper function to check if all files are received and emit completion signal
      const checkAndSignalCompletion = () => {
        if (filesReceived >= totalFiles) {
          socketManager.emit('_internal_streaming_complete', {});
        }
      };

      // FIX P3: Register ALL streaming event handlers EARLY (before any HTTP requests)
      // This prevents event loss when backend starts emitting immediately

      fileResultHandler = (data: AdjustmentsSocketData) => {
        if (!uploadIdsList.includes(data.uploadId)) {
          return;
        }

        // Add info_record
        if (data.info_record) {
          streamingResults.info_df.push(data.info_record);
        }

        // Handle dataframe chunks
        if (data.chunked) {
          // Large dataset - chunks will arrive separately
          fileChunks.set(data.filename, {
            chunks: [],
            totalChunks: data.totalChunks ?? 0,
          });
          // Don't increment filesReceived yet - wait for dataframe_complete
        } else {
          // Small dataset - all data in one emit
          if (data.dataframe_chunk && data.dataframe_chunk.length > 0) {
            // Use concat to avoid stack overflow with large arrays
            streamingResults.dataframe = streamingResults.dataframe.concat(data.dataframe_chunk);
          }
          // For non-chunked data, increment immediately since all data is received
          filesReceived++;
          checkAndSignalCompletion();
        }
      };

      dataframeChunkHandler = (data: AdjustmentsSocketData) => {
        if (!uploadIdsList.includes(data.uploadId)) {
          return;
        }

        const chunkInfo = fileChunks.get(data.filename);
        if (chunkInfo) {
          chunkInfo.chunks.push({ index: data.chunkIndex ?? 0, data: data.chunk ?? [] });
        }
      };

      dataframeCompleteHandler = (data: AdjustmentsSocketData) => {
        if (!uploadIdsList.includes(data.uploadId)) {
          return;
        }

        const chunkInfo = fileChunks.get(data.filename);
        if (chunkInfo) {
          // Sort chunks by index and combine
          const sortedChunks = chunkInfo.chunks.sort((a, b) => a.index - b.index);
          const combinedData = sortedChunks.flatMap((c) => c.data);

          // IMPORTANT: Use concat instead of spread operator to avoid stack overflow with large arrays
          streamingResults.dataframe = streamingResults.dataframe.concat(combinedData);

          // Cleanup
          fileChunks.delete(data.filename);

          // Increment filesReceived only when all chunks for this file are complete
          filesReceived++;
          checkAndSignalCompletion();
        }
      };

      fileErrorHandler = (data: AdjustmentsSocketData) => {
        if (!uploadIdsList.includes(data.uploadId)) return;

        failedFiles.push(data.filename);

        filesReceived++;

        // Check if all files are received (including errors) and signal completion
        checkAndSignalCompletion();
      };

      // FIX P3: Register ALL streaming handlers BEFORE sending any requests
      socketManager.on('file_result', fileResultHandler);
      socketManager.on('dataframe_chunk', dataframeChunkHandler);
      socketManager.on('dataframe_complete', dataframeCompleteHandler);
      socketManager.on('file_error', fileErrorHandler);

      // Setup Socket.IO progress listener for processing phase
      progressHandler = (rawData) => {
        // Socket.IO payloads arrive untyped; assert the documented shape once.
        const data = rawData as unknown as AdjustmentsSocketData;
        // Check if this event is for any of our uploadIds
        if (uploadIdsList.includes(data.uploadId) && onProgress) {
          const rawProgress = Math.min(Math.max(data.progress || 0, 0), 100);

          // Prevent progress regression - only allow progress to go up
          if (data.status !== 'error' && rawProgress < this.highestProgress) {
            return;
          }

          if (rawProgress > this.highestProgress) {
            this.highestProgress = rawProgress;
          }

          // Translate message using messageKey if available, otherwise use message directly
          const translatedMessage = data.messageKey
            ? translateMessage(
                data.messageKey as string,
                data.messageParams as Record<string, unknown> | undefined,
                data.message as string | undefined,
                'dataAdjustments.progress'
              )
            : (data.message || '');

          // Translate detail using detailKey if available
          const translatedDetail = data.detailKey
            ? translateMessage(
                data.detailKey as string,
                data.detailParams as Record<string, unknown> | undefined,
                undefined,
                'dataAdjustments.progress'
              ) || undefined
            : data.detail;

          onProgress({
            uploadProgress: 100,                    // Upload is complete
            processingProgress: rawProgress,        // Backend processing progress (with regression prevention)
            status: 'processing',
            phase: data.phase || 'data_processing',
            message: translatedMessage,
            step: data.step,
            detail: translatedDetail,
            // Pass ETA and file tracking from backend
            eta: data.eta,
            etaFormatted: data.etaFormatted,
            currentFile: data.currentFile,
            totalFiles: data.totalFiles
          });
        }
      };

      socketManager.onProgress(progressHandler);

      // NOTE: Frontend progress updates REMOVED - using backend Socket.IO progress only
      // Backend provides detailed progress with ETA, file counts, etc.

      // Process each chunk for each file
      for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
        const startIdx = chunkIndex * CHUNK_SIZE;
        const endIdx = Math.min((chunkIndex + 1) * CHUNK_SIZE, fileNames.length);

        // Process each file separately
        for (const fileName of fileNames) {
          const data = {
            startTime: params.startTime,
            endTime: params.endTime,
            timeStepSize: params.timeStepSize,
            offset: params.offset,
            methods: {
              [fileName]: {
                method: params.methods[fileName]?.method || "",
                intrpl_max: params.methods[fileName]?.intrpl_max,
                decimal_precision: params.methods[fileName]?.decimal_precision || "full"
              },
            },
            upload_id: uploadIds[fileName],
            chunkInfo: {
              currentChunk: chunkIndex + 1,
              totalChunks,
              startIndex: startIdx,
              endIndex: endIdx,
            },
          };


          const response = await fetch(
            `${this.API_BASE_URL}/api/adjustmentsOfData/adjust-data-chunk`,
            {
              method: "POST",
              body: JSON.stringify(data),
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${accessToken}`
              },
            }
          );

          if (!response.ok) {
            let errorMsg = `Chunk ${chunkIndex + 1} processing failed for ${fileName}: HTTP ${response.status}`;
            let errorCode: string | undefined;
            let suggestion: string | undefined;
            try {
              const body = await response.clone().json();
              if (typeof body?.error === 'string') errorMsg = body.error;
              else if (typeof body?.message === 'string') errorMsg = body.message;
              if (typeof body?.error_code === 'string') errorCode = body.error_code;
              if (Array.isArray(body?.suggestions) && typeof body.suggestions[0] === 'string') {
                suggestion = body.suggestions[0];
              }
            } catch {
              /* response had no JSON body */
            }
            const err = new Error(errorMsg) as Error & { code?: string; status?: number; suggestion?: string };
            err.status = response.status;
            if (errorCode) err.code = errorCode;
            if (suggestion) err.suggestion = suggestion;
            throw err;
          }

          const chunkData = await response.json();
          
          // NOTE: Frontend progress updates REMOVED - using backend Socket.IO progress only
          // Backend sends detailed progress via 'processing_progress' event

          if (chunkData.success && chunkData.data) {
            // Received chunk data
          }
        }
      }

      // NOTE: Finalization progress REMOVED - backend sends 100% when complete

      // Send completion request ONCE for all files (they share same uploadId now)
      // Get the common uploadId (all files now share the same one)
      const commonUploadId = uploadIds[fileNames[0]];

      const response = await fetch(
        `${this.API_BASE_URL}/api/adjustmentsOfData/adjustdata/complete`,
        {
          method: "POST",
          body: JSON.stringify({
            uploadId: commonUploadId,
            totalChunks,
            files: fileNames,
            startTime: params.startTime,
            endTime: params.endTime,
            timeStepSize: params.timeStepSize,
            offset: params.offset,
            methods: params.methods
          }),
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${accessToken}`
          },
        }
      );

      const completionResponses = [{ fileName: fileNames.join(', '), response }];

      // Check if any requests failed
      const failedResponses = completionResponses.filter(
        ({ response }) => !response.ok
      );
      if (failedResponses.length > 0) {
        const errors = await Promise.all(
          failedResponses.map(async ({ fileName, response }) => {
            const errorData = await response.json();
            return `${fileName}: ${errorData.error || "Unknown error"}`;
          })
        );
        throw new Error(`File processing failed: ${errors.join(", ")}`);
      }

      // Process all responses and combine their data
      const processedResponses = await Promise.all(
        completionResponses.map(async ({ fileName, response }) => {
          const data = await response.json();
          // Response data received
          return { ...data, fileName };
        })
      );

      // IMPORTANT: Check if any response requires methods FIRST - if so, return immediately
      // This must be checked BEFORE streaming mode to avoid waiting for Socket.IO events that never arrive
      const methodsRequired = processedResponses.some(
        (response) => response.methodsRequired
      );

      if (methodsRequired) {
        // Find the first response that requires methods
        const responseRequiringMethods = processedResponses.find(
          (response) => response.methodsRequired
        );

        // NOTE: Methods required - this is a special case where we need to notify frontend
        // Backend doesn't send progress for this, so we keep this one frontend update

        onProgress?.({
          uploadProgress: 100,
          processingProgress: 50,
          status: 'completed',
          phase: 'data_processing',
          message: 'Methods required for data processing'
        });

        // FIX P9: Cleanup ALL handlers when methods required (early return path)
        if (progressHandler) socketManager.offProgress(progressHandler);
        if (fileResultHandler) socketManager.off('file_result', fileResultHandler);
        if (dataframeChunkHandler) socketManager.off('dataframe_chunk', dataframeChunkHandler);
        if (dataframeCompleteHandler) socketManager.off('dataframe_complete', dataframeCompleteHandler);
        if (fileErrorHandler) socketManager.off('file_error', fileErrorHandler);

        return {
          success: true,
          methodsRequired: true,
          data: responseRequiringMethods?.data || { info_df: [], dataframe: [] },
          message: responseRequiringMethods?.message || 'Methods required for processing'
        };
      }

      // STREAMING SUPPORT: Check if backend is using streaming mode
      const isStreaming = processedResponses.some((response) => response.streaming);

      if (isStreaming) {
        // Use streaming mode - WAIT for results that are being accumulated by EARLY handlers
        const streamingResult = await this.waitForStreamingResults(
          uploadIdsList,
          processedResponses[0].totalFiles,
          socketManager,
          onProgress,
          streamingResults,  // Pass existing storage
          filesReceived,      // Pass existing counter
          failedFiles,        // Pass existing failed files
          fileChunks          // Pass existing chunk storage
        );

        // Cleanup Socket.IO listeners and leave rooms
        if (progressHandler) {
          socketManager.offProgress(progressHandler);
        }

        uploadIdsList.forEach(uploadId => {
          socketManager.leaveRoom(uploadId);
        });

        return streamingResult;
      }

      // Combine all the responses (only if no methods required and not streaming)
      const combinedData = processedResponses.reduce(
        (acc, curr) => {
          if (curr.success && curr.data) {
            // Combining data

            const dataframeWithFilename = (curr.data.dataframe || []).map((item: { UTC: string; filename?: string; [key: string]: string | number | null | undefined }) => {
              if (!item.filename && curr.fileName) {
                return { ...item, filename: curr.fileName };
              }
              return item;
            });

            return {
              info_df: [...(acc.info_df || []), ...(curr.data.info_df || [])],
              dataframe: [
                ...(acc.dataframe || []),
                ...dataframeWithFilename,
              ],
            };
          }
          return acc;
        },
        { info_df: [], dataframe: [] }
      );

      // NOTE: Final progress update REMOVED - backend sends 100% via Socket.IO
      // Backend message: "Progress: 100% - Procesiranje završeno!"

      // Cleanup Socket.IO listener and leave rooms
      if (progressHandler) {
        socketManager.offProgress(progressHandler);
      }

      // Cleanup early-registered streaming handlers
      if (fileResultHandler) socketManager.off('file_result', fileResultHandler);
      if (dataframeChunkHandler) socketManager.off('dataframe_chunk', dataframeChunkHandler);
      if (dataframeCompleteHandler) socketManager.off('dataframe_complete', dataframeCompleteHandler);
      if (fileErrorHandler) socketManager.off('file_error', fileErrorHandler);

      // Leave all processing rooms
      uploadIdsList.forEach(uploadId => {
        socketManager.leaveRoom(uploadId);
      });

      return {
        success: true,
        methodsRequired: false, // If we reach here, no methods are required
        data: combinedData,
        message: 'Data processed successfully'
      };

    } catch (error) {
      // Cleanup Socket.IO listener and leave rooms in case of error
      const sm = this.getSocketManager();
      if (progressHandler) {
        sm.offProgress(progressHandler);
      }

      // Cleanup early-registered streaming handlers if they were defined
      if (fileResultHandler) sm.off('file_result', fileResultHandler);
      if (dataframeChunkHandler) sm.off('dataframe_chunk', dataframeChunkHandler);
      if (dataframeCompleteHandler) sm.off('dataframe_complete', dataframeCompleteHandler);
      if (fileErrorHandler) sm.off('file_error', fileErrorHandler);

      // Leave all processing rooms
      uploadIdsList.forEach(uploadId => {
        sm.leaveRoom(uploadId);
      });

      let errorMessage = 'Data processing error';

      if (!window.navigator.onLine) {
        errorMessage = 'No internet connection';
      } else if (error instanceof TypeError && error.message === "Failed to fetch") {
        errorMessage = 'Server not reachable';
      } else {
        errorMessage = `Processing error: ${error instanceof Error ? error.message : 'Unknown error'}`;
      }

      // NOTE: Error handling kept on frontend - backend doesn't send error via Socket.IO for HTTP failures

      onProgress?.({
        uploadProgress: 100,
        processingProgress: 0,
        status: 'error',
        phase: 'data_processing',
        error: errorMessage
      });

      return { error: errorMessage };
    }
  }

  /**
   * Provjera statusa uploada
   */
  public static async checkUploadStatus(uploadId: string): Promise<AdjustmentsServerResponse> {
    try {
      // Get authentication token
      const accessToken = await getAuthToken();

      const response = await fetch(`${this.API_BASE_URL}/api/adjustmentsOfData/upload-status/${uploadId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to check upload status: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      return {
        error: `Failed to check upload status: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }

  /**
   * Otkazivanje uploada
   */
  public static async cancelUpload(uploadId: string): Promise<AdjustmentsServerResponse> {
    try {
      // Get authentication token
      const accessToken = await getAuthToken();

      const response = await fetch(`${this.API_BASE_URL}/api/adjustmentsOfData/cancel-upload`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({ uploadId }),
      });

      if (!response.ok) {
        throw new Error(`Failed to cancel upload: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      return {
        error: `Failed to cancel upload: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }

  /**
   * Wait for streaming results from SocketIO
   * Backend emits results per-file via 'file_result' and 'dataframe_chunk' events
   *
   * IMPORTANT: This function now receives pre-existing storage from early-registered handlers
   * instead of creating new listeners. This prevents timing issues where events are missed.
   */
  private static async waitForStreamingResults(
    _uploadIdsList: string[],
    totalFiles: number,
    socketManager: SocketManager,
    _onProgress: ((progress: AdjustmentsUploadProgress) => void) | undefined,
    existingResults: { info_df: AdjustmentsInfoRow[]; dataframe: AdjustmentsDataframeRow[] },
    existingFilesReceived: number,
    existingFailedFiles: string[],
    _existingFileChunks: Map<string, { chunks: { index: number; data: AdjustmentsDataframeRow[] }[]; totalChunks: number }>
  ): Promise<AdjustmentsServerResponse> {
    return new Promise((resolve, reject) => {
      // IMPORTANT: Use references to existing storage, NOT copies
      // This allows us to see updates from the early-registered handlers

      // Check if data is already complete (handlers finished before we got here)
      if (existingFilesReceived >= totalFiles) {

        if (existingFailedFiles.length > 0) {
          return resolve({
            success: true,
            data: existingResults,
            message: `Processing completed with ${existingFailedFiles.length} failed file(s): ${existingFailedFiles.join(', ')}`,
            partialFailure: true,
            failedFiles: existingFailedFiles,
          });
        } else {
          return resolve({
            success: true,
            data: existingResults,
            message: 'Data processed successfully via streaming',
          });
        }
      }

      // Data not complete yet - we need to SET UP a completion signal
      // The early handlers will update existingFilesReceived, but we need to be notified
      // SOLUTION: Use a custom event that the early handlers emit when they update filesReceived

      // Listen for completion signal from early handlers
      const completionHandler = () => {
        clearTimeout(timeoutHandle);
        socketManager.off('_internal_streaming_complete', completionHandler);

        if (existingFailedFiles.length > 0) {
          resolve({
            success: true,
            data: existingResults,
            message: `Processing completed with ${existingFailedFiles.length} failed file(s): ${existingFailedFiles.join(', ')}`,
            partialFailure: true,
            failedFiles: existingFailedFiles,
          });
        } else {
          resolve({
            success: true,
            data: existingResults,
            message: 'Data processed successfully via streaming',
          });
        }
      };

      socketManager.on('_internal_streaming_complete', completionHandler);

      // Timeout safety (10 minutes for large datasets)
      const timeoutHandle = setTimeout(() => {
        socketManager.off('_internal_streaming_complete', completionHandler);
        reject(new Error('Streaming timeout - server not responding'));
      }, 600000);
    });
  }
}

export default AdjustmentsOfDataChunkedUploader;